import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell, Avatar, Button, Card, Icon, Input, SegmentedControl } from '../../ui';
import { usePerfis } from '../../servicos/perfis/usePerfil';
import { Perfil } from '../../servicos/perfis/typePerfil';
import { useAuth } from '../../contextos/AuthContext';
import { useAtualizacaoContexto } from '../../contextos/AtualizacaoContexto';
import useAvisos from '../../hooks/useAviso';
import ApagarDadosSheet from './ApagarDadosSheet';
import styles from './Configuracoes.module.css';

// Únicos hex permitidos fora de token: as opções de cor de destaque.
const COR_PADRAO = '#6366f1'; // indigo (default do design system)
const CORES_DESTAQUE = [
  { hex: '#2b82ff', nome: 'Azul' },
  { hex: '#8b5cf6', nome: 'Violeta' },
  { hex: '#059669', nome: 'Verde' },
  { hex: '#d97706', nome: 'Âmbar' },
  { hex: '#dc2626', nome: 'Vermelho' },
  { hex: '#0891b2', nome: 'Ciano' }
];

const OPCOES_TEMA = [
  { id: 'light', rotulo: 'Claro', icone: 'light_mode' },
  { id: 'dark', rotulo: 'Escuro', icone: 'dark_mode' }
];

/**
 * Configurações — tela dedicada que substitui o modal antigo de perfil.
 * Perfil, aparência (tema + cor de destaque), segurança, sessão e sobre.
 */
export default function Configuracoes() {
  const { perfil, salvando, updatePerfil } = usePerfis();
  const { usuario, esqueciSenha, processando, sair } = useAuth();
  const { setAtualizar } = useAtualizacaoContexto();
  const { adicionarAviso } = useAvisos();
  const navigate = useNavigate();

  const [dados, setDados] = useState<Perfil>(perfil);
  const [tema, setTema] = useState<string>(localStorage.getItem('tema') ?? 'light');
  const [cor, setCor] = useState<string | null>(localStorage.getItem('cor'));
  const [linkEnviado, setLinkEnviado] = useState<boolean>(false);
  const [apagarAberto, setApagarAberto] = useState<boolean>(false);

  // Sincroniza o formulário quando o perfil termina de carregar/atualizar.
  useEffect(() => {
    setDados(perfil);
  }, [perfil]);

  const email = usuario?.email ?? perfil.email;

  const salvarPerfil = async () => {
    if (!perfil?.id) return;
    // Mesmo payload do fluxo atual: perfil completo com os campos editados.
    await updatePerfil(perfil.id, { ...dados });
  };

  const aplicarTema = (novoTema: string) => {
    setTema(novoTema);
    localStorage.setItem('tema', novoTema);
    document.documentElement.setAttribute('data-theme', novoTema);
    setAtualizar((atual) => !atual);
  };

  const aplicarCor = (hex: string) => {
    setCor(hex);
    localStorage.setItem('cor', hex);
    const root = document.documentElement;
    root.style.setProperty('--primary', hex);
    root.style.setProperty('--secondary', hex + '10');
    setAtualizar((atual) => !atual);
  };

  const limparCor = () => {
    setCor(null);
    localStorage.removeItem('cor');
    // Remove os overrides inline: voltam os tokens do tema (claro/escuro).
    const root = document.documentElement;
    root.style.removeProperty('--primary');
    root.style.removeProperty('--secondary');
    setAtualizar((atual) => !atual);
  };

  const enviarLinkSenha = async () => {
    if (!email) {
      adicionarAviso('E-mail da conta não encontrado.', 'erro');
      return;
    }
    const resultado = await esqueciSenha(email);
    if (resultado.ok) {
      setLinkEnviado(true);
    } else {
      adicionarAviso(resultado.mensagem || 'Não foi possível enviar o e-mail.', 'erro');
    }
  };

  const sairDaConta = () => {
    sair();
    navigate('/');
  };

  return (
    <AppShell titulo="Configurações" subtitulo="Sua conta, aparência e preferências do Finant.">
      <div className={styles.coluna}>
        {/* Perfil */}
        <Card className={styles.secao}>
          <div className={styles.secaoCabecalho}>
            <h2 className={styles.secaoTitulo}>Perfil</h2>
            <p className={styles.secaoDescricao}>Como você aparece no Finant.</p>
          </div>
          <div className={styles.perfilTopo}>
            <Avatar nome={dados.nome ?? email} tamanho={64} />
            <div className={styles.perfilNomes}>
              <span className={styles.perfilNome}>{dados.nome || 'Sem nome ainda'}</span>
              <span className={styles.perfilEmail}>{email}</span>
            </div>
          </div>
          <div className={styles.formulario}>
            <Input
              label="Nome"
              icone="person"
              valor={dados.nome ?? ''}
              onChange={(valor) => setDados({ ...dados, nome: valor })}
              placeholder="Seu nome"
            />
            <Input
              label="Telefone"
              icone="call"
              valor={dados.telefone ?? ''}
              onChange={(valor) => setDados({ ...dados, telefone: valor })}
              placeholder="(00) 00000-0000"
            />
            <Input
              label="Aniversário"
              icone="cake"
              tipo="date"
              valor={(dados.aniversario ?? '').slice(0, 10)}
              onChange={(valor) => setDados({ ...dados, aniversario: valor })}
            />
          </div>
          <div className={styles.acoesFormulario}>
            <Button variante="primaria" icone="check" carregando={salvando} onClick={salvarPerfil}>
              Salvar
            </Button>
          </div>
        </Card>

        {/* Aparência */}
        <Card className={styles.secao}>
          <div className={styles.secaoCabecalho}>
            <h2 className={styles.secaoTitulo}>Aparência</h2>
            <p className={styles.secaoDescricao}>Tema e cor de destaque, aplicados na hora.</p>
          </div>
          <div className={styles.campo}>
            <span className={styles.campoRotulo}>Tema</span>
            <SegmentedControl opcoes={OPCOES_TEMA} valor={tema} onChange={aplicarTema} />
          </div>
          <div className={styles.campo}>
            <span className={styles.campoRotulo}>Cor de destaque</span>
            <div className={styles.swatches}>
              <button
                type="button"
                className={`${styles.padrao} ${cor === null ? styles.swatchAtivo : ''}`}
                onClick={limparCor}
              >
                <span className={styles.pontoPadrao} style={{ background: COR_PADRAO }} />
                Padrão
              </button>
              {CORES_DESTAQUE.map((opcao) => (
                <button
                  key={opcao.hex}
                  type="button"
                  className={`${styles.swatch} ${cor === opcao.hex ? styles.swatchAtivo : ''}`}
                  style={{ background: opcao.hex }}
                  onClick={() => aplicarCor(opcao.hex)}
                  title={opcao.nome}
                  aria-label={`Cor ${opcao.nome}`}
                />
              ))}
            </div>
          </div>
        </Card>

        {/* Segurança */}
        <Card className={styles.secao}>
          <div className={styles.secaoCabecalho}>
            <h2 className={styles.secaoTitulo}>Segurança</h2>
            <p className={styles.secaoDescricao}>
              Por segurança, a troca de senha é feita por um link enviado ao seu e-mail.
            </p>
          </div>
          <div className={styles.emailLinha}>
            <span className={styles.emailRotulo}>E-mail da conta</span>
            <span className={styles.emailValor}>{email ?? '—'}</span>
          </div>
          <div className={styles.acoesSeguranca}>
            <Button variante="contorno" icone="mail" carregando={processando} onClick={enviarLinkSenha}>
              Enviar link para redefinir senha
            </Button>
            {linkEnviado && (
              <span className={styles.confirmacao}>
                <Icon nome="mark_email_read" tamanho={18} />
                Link enviado para {email}. Verifique sua caixa de entrada.
              </span>
            )}
          </div>
        </Card>

        {/* Sessão */}
        <Card className={styles.secao}>
          <div className={styles.secaoCabecalho}>
            <h2 className={styles.secaoTitulo}>Sessão</h2>
            <p className={styles.secaoDescricao}>Encerrar a sessão neste dispositivo.</p>
          </div>
          <div className={styles.sessaoAcao}>
            <Button variante="perigo" icone="logout" onClick={sairDaConta}>
              Sair da conta
            </Button>
          </div>
        </Card>

        {/* Zona de perigo */}
        <Card className={`${styles.secao} ${styles.zonaPerigo}`}>
          <div className={styles.secaoCabecalho}>
            <h2 className={`${styles.secaoTitulo} ${styles.tituloPerigo}`}>Zona de perigo</h2>
            <p className={styles.secaoDescricao}>
              Apague todos os dados da sua conta ou apenas parte deles. A ação exige a sua senha
              e não pode ser desfeita.
            </p>
          </div>
          <div className={styles.sessaoAcao}>
            <Button variante="perigo" icone="delete_forever" onClick={() => setApagarAberto(true)}>
              Apagar dados…
            </Button>
          </div>
        </Card>

        {/* Sobre */}
        <Card className={styles.secao}>
          <div className={styles.secaoCabecalho}>
            <h2 className={styles.secaoTitulo}>Sobre</h2>
          </div>
          <div className={styles.sobreLinha}>
            <span className={styles.versao}>Finant</span>
            <button type="button" className={styles.link} onClick={() => navigate('/termos-e-politicas')}>
              Termos e políticas
            </button>
          </div>
        </Card>
      </div>

      <ApagarDadosSheet aberto={apagarAberto} onFechar={() => setApagarAberto(false)} />
    </AppShell>
  );
}
